import { ApiError, apiFetch } from './api-fetch.ts'

// The admin panel is deliberately JSON-first, same rationale as
// `sessions-api.ts`/`registry-api.ts` — these interfaces cover the fields
// the panel itself reads/displays; the rest of the row rides through as
// JSON.
export interface AlertRow {
  id: string
  rootId: string
  kind: string
  entityType: string
  entityId: string
  message: string
  status: string
  createdAt: string
  updatedAt: string
  [key: string]: unknown
}

export type AlertStatus = 'open' | 'acknowledged' | 'resolved'

export interface DashboardSessionRow {
  id: string
  status: string
  [key: string]: unknown
}

export interface DashboardHarness {
  id: string
  name: string
  status: 'unregistered' | 'online' | 'offline'
  lastHeartbeatAt: string | null
}

export interface Dashboard {
  sessions: {
    active: DashboardSessionRow[]
    counts: { byStatus: Record<string, number> }
  }
  alerts: {
    open: AlertRow[]
    counts: { byStatus: Record<string, number> }
  }
  harnesses: DashboardHarness[]
  staleSessions: string[]
  pendingApprovals: string[]
}

export interface RunChecksResult {
  opened: number
  resolved: number
}

export function getDashboard(rootId?: string): Promise<Dashboard> {
  const query = rootId ? `?rootId=${encodeURIComponent(rootId)}` : ''
  return apiFetch<Dashboard>(`/dashboard${query}`)
}

export async function listAlerts(
  status?: AlertStatus,
  rootId?: string,
): Promise<AlertRow[]> {
  const params = new URLSearchParams()
  if (status) params.set('status', status)
  if (rootId) params.set('rootId', rootId)
  const query = params.toString() ? `?${params.toString()}` : ''
  const { items } = await apiFetch<{ items: AlertRow[] }>(`/alerts${query}`)
  return items
}

export function acknowledgeAlert(id: string): Promise<AlertRow> {
  return apiFetch<AlertRow>(`/alerts/${id}/acknowledge`, {
    method: 'POST',
  })
}

export function resolveAlert(id: string): Promise<AlertRow> {
  return apiFetch<AlertRow>(`/alerts/${id}/resolve`, { method: 'POST' })
}

export function runAlertChecks(): Promise<RunChecksResult> {
  return apiFetch<RunChecksResult>('/alerts/run-checks', {
    method: 'POST',
  })
}

// `ApiError` moved to `api-fetch.ts` with the shared fetch; re-exported here
// so existing importers of this module keep working.
export { ApiError }
